import React from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../../api/client';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { unwrapArray } from '../../lib/api-envelope';
import { formatCalendarDate } from '../../lib/calendar-date';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import type { SalesActivity, SalesActivityListParams } from '../../types/sales-activity';

/**
 * The reader's own open follow-ups (CRM-FE-4): the "My Follow-ups" Overview
 * widget, and the same list on the Meetings page for a sales user, who has no
 * Overview.
 *
 * THE GROUPS ARE THE API'S ANSWERS, NOT DATE MATHS. Three questions, all
 * `mine=1`: `follow_up=overdue` (before the tenant's today), `follow_up=due`
 * (today or before), `follow_up=open` (every open one, soonest first).
 *   Overdue   = the overdue answer
 *   Due today = in the due answer and not in the overdue one
 *   Coming up = in the open answer and not in the due one — the API has no
 *               "next 7 days" question, so this lists the soonest few rather
 *               than claim a window it cannot draw
 * Done is one tap: PATCH follow_up_done, then every list is re-asked.
 */
const COMING_UP_SHOWN = 5;

function useMine(follow_up: NonNullable<SalesActivityListParams['follow_up']>) {
  return useQuery({
    queryKey: ['sales-activities', 'list', { mine: 1, follow_up }],
    queryFn: () => api.salesActivities.list({ mine: 1, follow_up }).then((r) => unwrapArray<SalesActivity>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function MyFollowUpsList() {
  const queryClient = useQueryClient();
  const overdueQ = useMine('overdue');
  const dueQ = useMine('due');
  const openQ = useMine('open');
  const failedQ = [overdueQ, dueQ, openQ].find((q) => queryLoadFailure(q));

  const done = useMutation({
    mutationFn: (id: string) => api.salesActivities.update(id, { follow_up_done: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales-activities'] }),
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not mark it done.')),
  });

  if (failedQ) return <LoadFailureNotice error={queryFailureError(failedQ)} subject="your follow-ups" className="py-6" />;
  if (!overdueQ.data || !dueQ.data || !openQ.data) return <div className="h-16 animate-pulse rounded-lg bg-fieldiq-dark/60" aria-busy="true" />;

  const overdueIds = new Set(overdueQ.data.map((r) => r.id));
  const dueIds = new Set(dueQ.data.map((r) => r.id));
  const groups: { key: string; title: string; rows: SalesActivity[] }[] = [
    { key: 'overdue', title: 'Overdue', rows: overdueQ.data },
    { key: 'today', title: 'Due today', rows: dueQ.data.filter((r) => !overdueIds.has(r.id)) },
    { key: 'upcoming', title: 'Coming up', rows: openQ.data.filter((r) => !dueIds.has(r.id)).slice(0, COMING_UP_SHOWN) },
  ];
  if (groups.every((g) => g.rows.length === 0)) {
    return <p className="py-4 text-sm text-fieldiq-muted">No follow-ups due</p>;
  }

  return (
    <div className="space-y-4">
      {groups.filter((g) => g.rows.length > 0).map((g) => (
        <section key={g.key} aria-label={g.title} data-testid={`follow-ups-${g.key}`}>
          <h4 className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{g.title}</h4>
          <ul className="mt-1 divide-y divide-fieldiq-border">
            {g.rows.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <Link to={`/sales/meetings/${r.id}`} className="min-w-0 hover:underline">
                  <span className="block truncate font-semibold text-white">{r.customerName ?? '—'}</span>
                  <span className="block truncate text-fieldiq-muted">
                    {formatCalendarDate(r.followUpDate) ?? '—'}{r.nextStep ? ` · ${r.nextStep}` : ''}
                  </span>
                </Link>
                <button
                  type="button"
                  aria-label={`Mark the follow-up with ${r.customerName ?? 'this customer'} done`}
                  disabled={done.isPending}
                  onClick={() => done.mutate(r.id)}
                  className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg border border-fieldiq-border bg-fieldiq-dark px-2.5 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                >
                  <Check size={12} aria-hidden="true" /> Done
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export default MyFollowUpsList;
